import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  TrendingUp, 
  Plus,
  Eye,
  IndianRupee,
  AlertCircle
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { invoicesAPI } from '../services/api';

// Custom Receipt Icon with Rupee symbol
const ReceiptIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
    <path d="M14 8H8" />
    <path d="M16 12H8" />
    <path d="M13 16H8" />
    <text x="15.5" y="9.5" fontSize="7" fontWeight="bold" fill="currentColor" textAnchor="middle">₹</text>
  </svg>
);

interface Analytics {
  totalInvoices: number;
  totalAmount: number;
  paidInvoices: number;
  unpaidInvoices: number;
  overdueInvoices: number;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  client: {
    clientName: string;
    email: string;
  };
  totalAmount: number;
  status: string;
  items: Array<{
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
}

const COLORS = [
  'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
  'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
  'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)'
];

// For pie chart cells, we need solid colors
const SOLID_COLORS = ['#6366f1', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6'];

const Dashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  useEffect(() => {
    // Set default date range to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    // Fetch data after a short delay to ensure state is set
    const timer = setTimeout(() => {
      setSelectedPeriod('30d');
      fetchDashboardData();
    }, 100);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only fetch if we have a period selected and dates are set (or period is 'all')
    if (selectedPeriod) {
      if (selectedPeriod === 'all' || (startDate && endDate)) {
        fetchDashboardData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, startDate, endDate]);

  const handlePeriodChange = (period: '7d' | '30d' | '90d' | 'all') => {
    const end = new Date();
    const start = new Date();
    
    if (period === 'all') {
      setStartDate('');
      setEndDate('');
    } else {
      start.setDate(start.getDate() - (period === '7d' ? 7 : period === '90d' ? 90 : 30));
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
    
    // Update period after setting dates with a small delay to ensure state is updated
    setTimeout(() => {
      setSelectedPeriod(period);
    }, 0);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: 1000
      };
      
      // Only add date filters if not "all" period
      if (selectedPeriod !== 'all' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      console.log('Fetching dashboard data with params:', params);

      const [analyticsRes, invoicesRes] = await Promise.all([
        invoicesAPI.getAnalytics(params),
        invoicesAPI.getAll(params)
      ]);
      
      console.log('Analytics data:', analyticsRes.data);
      console.log('Invoices data:', invoicesRes.data.invoices?.length);
      
      setAnalytics(analyticsRes.data);
      setAllInvoices(invoicesRes.data.invoices || []);
      setRecentInvoices(invoicesRes.data.invoices?.slice(0, 5) || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts
  const getMonthlyRevenue = () => {
    const monthlyData: { [key: string]: number } = {};
    
    allInvoices.forEach(invoice => {
      const date = new Date(invoice.invoiceDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + invoice.totalAmount;
    });

    return Object.entries(monthlyData)
      .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(-12); // Last 12 months
  };

  const getStatusDistribution = () => {
    const statusData = {
      'Paid': 0,
      'Sent': 0,
      'Overdue': 0,
      'Draft': 0
    };

    allInvoices.forEach(invoice => {
      const status = invoice.status as keyof typeof statusData;
      if (status in statusData) {
        statusData[status]++;
      }
    });

    return [
      { name: 'Paid', value: statusData.Paid, color: SOLID_COLORS[1] },
      { name: 'Sent', value: statusData.Sent, color: SOLID_COLORS[0] },
      { name: 'Overdue', value: statusData.Overdue, color: SOLID_COLORS[2] },
      { name: 'Draft', value: statusData.Draft, color: SOLID_COLORS[3] }
    ].filter(item => item.value > 0);
  };

  const getDailyRevenue = () => {
    const dailyData: { [key: string]: { paid: number; total: number } } = {};
    
    allInvoices.forEach(invoice => {
      const date = new Date(invoice.invoiceDate);
      const dateKey = date.toISOString().split('T')[0];
      
      if (!dailyData[dateKey]) {
        dailyData[dateKey] = { paid: 0, total: 0 };
      }
      
      dailyData[dateKey].total += invoice.totalAmount;
      if (invoice.status === 'Paid') {
        dailyData[dateKey].paid += invoice.totalAmount;
      }
    });

    return Object.entries(dailyData)
      .map(([date, data]) => ({
        date,
        paid: Number(data.paid.toFixed(2)),
        pending: Number((data.total - data.paid).toFixed(2))
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30); // Last 30 days
  };

  const getTopClients = () => {
    const clientData: { [key: string]: number } = {};
    
    allInvoices.forEach(invoice => {
      const clientName = invoice.client.clientName;
      clientData[clientName] = (clientData[clientName] || 0) + invoice.totalAmount;
    });

    return Object.entries(clientData)
      .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'Sent':
        return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'Overdue':
        return 'bg-rose-100 text-rose-700 border border-rose-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  const calculateCollectionRate = () => {
    const paidAmount = allInvoices
      .filter(inv => inv.status === 'Paid')
      .reduce((sum, inv) => sum + inv.totalAmount, 0);
    
    const totalAmount = allInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    
    return totalAmount > 0 ? ((paidAmount / totalAmount) * 100).toFixed(1) : '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-3 border-indigo-200 border-t-indigo-600"></div>
      </div>
    );
  }

  const monthlyRevenue = getMonthlyRevenue();
  const statusDistribution = getStatusDistribution();
  const dailyRevenue = getDailyRevenue();
  const topClients = getTopClients();
  const collectionRate = calculateCollectionRate();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-slate-900 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Financial Dashboard
          </h1>
          <p className="text-slate-600 text-lg">Complete overview of your business finances</p>
        </div>
        
        {/* Date Range Selector */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm">
            <button
              onClick={() => handlePeriodChange('7d')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                selectedPeriod === '7d' 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30' 
                  : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => handlePeriodChange('30d')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                selectedPeriod === '30d' 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30' 
                  : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
              }`}
            >
              30 Days
            </button>
            <button
              onClick={() => handlePeriodChange('90d')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                selectedPeriod === '90d' 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30' 
                  : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
              }`}
            >
              90 Days
            </button>
            <button
              onClick={() => handlePeriodChange('all')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                selectedPeriod === 'all' 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30' 
                  : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
              }`}
            >
              All Time
            </button>
          </div>
          <Link
            to="/invoice/create"
            className="btn-primary flex items-center space-x-2 shadow-lg"
          >
            <Plus className="h-5 w-5" />
            <span>Create Invoice</span>
          </Link>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-full blur-3xl group-hover:blur-2xl transition-all"></div>
          <div className="flex items-center justify-between gap-3 relative z-10">
            <div>
              <p className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">Total Revenue</p>
              <p className="text-2xl sm:text-3xl xl:text-4xl font-bold break-words min-w-0 text-slate-900 mb-1">
                {formatCurrency(analytics?.totalAmount || 0)}
              </p>
              {allInvoices.length > 0 && (
                <p className="text-sm text-slate-500 mt-2">
                  Avg: {formatCurrency((analytics?.totalAmount || 0) / allInvoices.length)}
                </p>
              )}
            </div>
            <div className="p-5 shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg shadow-indigo-500/30">
              <IndianRupee className="h-8 w-8 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl group-hover:blur-2xl transition-all"></div>
          <div className="flex items-center justify-between gap-3 relative z-10">
            <div>
              <p className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">Collection Rate</p>
              <p className="text-2xl sm:text-3xl xl:text-4xl font-bold break-words min-w-0 text-emerald-600 mb-1">
                {collectionRate}%
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {analytics?.paidInvoices || 0} paid invoices
              </p>
            </div>
            <div className="p-5 shrink-0 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/30">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-full blur-3xl group-hover:blur-2xl transition-all"></div>
          <div className="flex items-center justify-between gap-3 relative z-10">
            <div>
              <p className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">Pending Payments</p>
              <p className="text-2xl sm:text-3xl xl:text-4xl font-bold break-words min-w-0 text-amber-600 mb-1">
                {analytics?.unpaidInvoices || 0}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {formatCurrency(allInvoices
                  .filter(inv => inv.status !== 'Paid')
                  .reduce((sum, inv) => sum + inv.totalAmount, 0)
                )}
              </p>
            </div>
            <div className="p-5 shrink-0 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/30">
              <IndianRupee className="h-8 w-8 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-rose-500/10 to-red-500/10 rounded-full blur-3xl group-hover:blur-2xl transition-all"></div>
          <div className="flex items-center justify-between gap-3 relative z-10">
            <div>
              <p className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">Overdue</p>
              <p className="text-2xl sm:text-3xl xl:text-4xl font-bold break-words min-w-0 text-rose-600 mb-1">
                {analytics?.overdueInvoices || 0}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Needs attention
              </p>
            </div>
            <div className="p-5 shrink-0 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl shadow-lg shadow-rose-500/30">
              <AlertCircle className="h-8 w-8 text-white" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <h3 className="text-xl font-bold text-slate-900 mb-6 relative z-10">Monthly Revenue Trend</h3>
          <div className="relative z-10">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={monthlyRevenue} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4}/>
                    <stop offset="50%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#ec4899" stopOpacity={0.1}/>
                  </linearGradient>
                  <filter id="shadow">
                    <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#6366f1" floodOpacity="0.3"/>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.6} />
                <XAxis 
                  dataKey="name" 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }} 
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <YAxis 
                  tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }} 
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: 'none', 
                    borderRadius: '1rem',
                    color: '#1e293b',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                  cursor={{ stroke: '#6366f1', strokeWidth: 2, strokeDasharray: '5 5' }}
                  formatter={(value: number) => [formatCurrency(value), 'Revenue']} 
                  labelStyle={{ color: '#6366f1', fontWeight: 700, marginBottom: '8px' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="url(#strokeGradient)" 
                  strokeWidth={4} 
                  fillOpacity={1} 
                  fill="url(#colorRevenue)"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <defs>
                  <linearGradient id="strokeGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6366f1"/>
                    <stop offset="50%" stopColor="#8b5cf6"/>
                    <stop offset="100%" stopColor="#ec4899"/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Status Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <h3 className="text-xl font-bold text-slate-900 mb-6 relative z-10">Invoice Status Distribution</h3>
          <div className="relative z-10">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <defs>
                  <filter id="pieShadow">
                    <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#000" floodOpacity="0.15"/>
                  </filter>
                </defs>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name}\n${(percent * 100).toFixed(0)}%`}
                  outerRadius={110}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  animationDuration={1500}
                  animationEasing="ease-out"
                  style={{ filter: 'url(#pieShadow)' }}
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color}
                      stroke="#ffffff"
                      strokeWidth={3}
                      style={{ 
                        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: 'none', 
                    borderRadius: '1rem',
                    color: '#1e293b',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                  formatter={(value: number, name: string) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Daily Revenue Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-red-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <h3 className="text-xl font-bold text-slate-900 mb-6 relative z-10">Daily Revenue (Paid vs Pending)</h3>
          <div className="relative z-10">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={dailyRevenue} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="paidGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0.05}/>
                  </linearGradient>
                  <filter id="lineShadow">
                    <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.1"/>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.6} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }} 
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <YAxis 
                  tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }} 
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: 'none', 
                    borderRadius: '1rem',
                    color: '#1e293b',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                  cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '5 5' }}
                  formatter={(value: number, name: string) => [formatCurrency(value), name]} 
                  labelStyle={{ color: '#64748b', fontWeight: 700, marginBottom: '8px' }}
                />
                <Legend 
                  wrapperStyle={{ color: '#475569', fontSize: '13px', fontWeight: 600, paddingTop: '20px' }}
                  iconType="circle"
                />
                <Line 
                  type="monotone" 
                  dataKey="paid" 
                  stroke="#10b981" 
                  strokeWidth={4} 
                  name="Paid" 
                  dot={{ fill: '#10b981', r: 6, strokeWidth: 2, stroke: '#ffffff' }}
                  activeDot={{ r: 8, stroke: '#10b981', strokeWidth: 2, fill: '#ffffff' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                  style={{ filter: 'url(#lineShadow)' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="pending" 
                  stroke="#ef4444" 
                  strokeWidth={4} 
                  name="Pending" 
                  dot={{ fill: '#ef4444', r: 6, strokeWidth: 2, stroke: '#ffffff' }}
                  activeDot={{ r: 8, stroke: '#ef4444', strokeWidth: 2, fill: '#ffffff' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                  style={{ filter: 'url(#lineShadow)' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Top Clients */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="card relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <h3 className="text-xl font-bold text-slate-900 mb-6 relative z-10">Top Clients by Revenue</h3>
          <div className="relative z-10">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={topClients} layout="vertical" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="50%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                  <filter id="barShadow">
                    <feDropShadow dx="2" dy="4" stdDeviation="6" floodColor="#6366f1" floodOpacity="0.3"/>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.6} />
                <XAxis 
                  type="number" 
                  tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }} 
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={120} 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }} 
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: 'none', 
                    borderRadius: '1rem',
                    color: '#1e293b',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                  cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }}
                  formatter={(value: number) => [formatCurrency(value), 'Revenue']} 
                  labelStyle={{ color: '#6366f1', fontWeight: 700, marginBottom: '8px' }}
                />
                <Bar 
                  dataKey="value" 
                  fill="url(#barGradient)" 
                  radius={[0, 16, 16, 0]}
                  animationDuration={1500}
                  animationEasing="ease-out"
                  style={{ filter: 'url(#barShadow)', cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Recent Invoices */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Recent Invoices</h2>
          <Link
            to="/invoices"
            className="text-indigo-600 hover:text-indigo-700 text-sm font-semibold flex items-center space-x-1 transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>View all</span>
          </Link>
        </div>

        {recentInvoices.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl mb-4">
              <FileText className="h-16 w-16 text-indigo-600" />
            </div>
            <p className="text-slate-600 mb-6 text-lg">No invoices yet</p>
            <Link
              to="/invoice/create"
              className="btn-primary mt-4 inline-flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Create your first invoice</span>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="table-header">Invoice</th>
                  <th className="table-header">Client</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {recentInvoices.map((invoice) => (
                  <tr key={invoice._id} className="hover:bg-slate-50 transition-colors">
                    <td className="table-cell font-semibold text-slate-900">
                      {invoice.invoiceNumber}
                    </td>
                    <td className="table-cell text-slate-700">{invoice.client.clientName}</td>
                    <td className="table-cell text-slate-600">
                      {new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}
                    </td>
                    <td className="table-cell font-semibold text-slate-900">
                      {formatCurrency(invoice.totalAmount)}
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-full ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/invoice/${invoice._id}`}
                          className="text-indigo-600 hover:text-indigo-700 transition-colors p-2 hover:bg-indigo-50 rounded-lg"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;
